import type { AccessReviewRow } from '@/shared/access';

import type { DepartmentRights } from './departmentRights';
import type { DepartmentFlag } from './reviewFlags';

/**
 * The evidence half of the access review.
 *
 * <p>Both exports carry the *filtered* rows, never the whole tenant: the reviewer is attaching evidence
 * for the decision they just made, and an export that quietly contained rows they never looked at would
 * misrepresent the review.
 *
 * <p>There are two of them because the screen asks two questions. The people export is the record of who
 * ended up with what; the department export is the one the screen's own note promises — confirming that a
 * department is still correct is recorded nowhere in the platform, so the export of what each department
 * conferred at review time is the only evidence an unchanged department can have.
 *
 * <p>Values stay raw (`ADMIN`, `orders.refund`) rather than translated: an export is read by a
 * spreadsheet and an auditor, and a level whose spelling depends on the reviewer's UI language cannot be
 * compared with last quarter's.
 */

/** One department, reduced to what the export needs. */
export interface DepartmentCsvRow {
  name: string;
  path?: string;
  memberCount: number;
  rights: DepartmentRights;
  flags: DepartmentFlag[];
}

/** RFC-4180 quoting: every cell quoted, embedded quotes doubled. */
export function toCsv(lines: string[][]): string {
  return lines
    .map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');
}

export function buildPeopleCsv(rows: AccessReviewRow[], domainCodes: string[]): string {
  const header = ['email', 'name', 'status', ...domainCodes];
  const lines = rows.map((row) => [
    row.email,
    row.name ?? '',
    row.status,
    ...domainCodes.map((code) => row.domains.find((d) => d.domainCode === code)?.level ?? 'NONE'),
  ]);
  return toCsv([header, ...lines]);
}

export function buildDepartmentsCsv(rows: DepartmentCsvRow[]): string {
  const header = [
    'department',
    'path',
    'members',
    'rights',
    'highRiskCapabilities',
    'needsAttention',
  ];
  const lines = rows.map((row) => [
    row.name,
    row.path ?? '',
    String(row.memberCount),
    row.rights.domains.map((entry) => `${entry.domainCode}:${entry.level}`).join('; '),
    row.rights.elevated.map((entry) => `${entry.domainCode}.${entry.capabilityCode}`).join('; '),
    row.flags.join('; '),
  ]);
  return toCsv([header, ...lines]);
}

/** Hands the CSV to the browser as a download. Separated from the builders so those stay testable. */
export function downloadCsv(csv: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
